
'use client';

import { motion, useMotionTemplate, useMotionValue } from 'framer-motion';
import type { MouseEvent, PropsWithChildren } from 'react';
import { cn } from '@/lib/utils';

interface SpotlightCardProps extends PropsWithChildren {
  variant?: 'default' | 'rainbow' | 'neon' | 'fire';
  className?: string;
}

export function SpotlightCard({ children, variant = 'default', className }: SpotlightCardProps) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function handleMouseMove({ currentTarget, clientX, clientY }: MouseEvent) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  const getGradientClass = () => {
    switch (variant) {
      case 'rainbow':
        return 'bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 to-purple-500';
      case 'neon':
        return 'bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600';
      case 'fire':
        return 'bg-gradient-to-r from-orange-500 via-red-500 to-pink-500';
      default:
        return 'bg-gradient-to-r from-primary via-accent to-primary';
    }
  };

  return (
    <div
      className={cn(
        "group relative w-full rounded-lg bg-card text-card-foreground shadow-sm transition-all duration-300 hover:shadow-2xl",
        className
      )}
      onMouseMove={handleMouseMove}
    >
      {/* Gradient Border */}
      <div className={`absolute -inset-0.5 rounded-lg ${getGradientClass()} opacity-25 blur-sm group-hover:opacity-100 transition-opacity duration-300`}></div>

      {/* Card Content with Border */}
      <div className="relative rounded-lg bg-card border border-transparent">
        <motion.div
          className="pointer-events-none absolute -inset-px rounded-lg opacity-0 transition duration-300 group-hover:opacity-100"
          style={{
            background: useMotionTemplate`
              radial-gradient(
                350px circle at ${mouseX}px ${mouseY}px,
                hsla(var(--primary) / 0.15),
                transparent 80%
              )
            `,
          }}
        />
        <div className="relative z-10 p-6">{children}</div>
      </div>
    </div>
  );
}
